import FLUSH_THEME from './flush';
import GENERIC_THEME from './generic';
import FOMOIO_THEME from './fomoio';
import LUNA_THEME from './luna';
import ROCKETBET_THEME from './rocketbet';
import TURBOSLOTS_THEME from './turboslots';
// Guard `process` — this module is now imported into browser bundles (via index.ts's
// getSportmodityColorVars), where `process` is undefined.
export const theme = (typeof process !== 'undefined' ? process.env.VITE_THEME : undefined) || 'flush';
// Small utility to safely access nested keys (like lodash.get)
const get = (obj, path, fallback) => {
    const result = path.split('.').reduce((o, k) => {
        if (o && typeof o === 'object' && k in o) {
            return o[k];
        }
        return undefined;
    }, obj);
    return result ?? fallback;
};
/**
 * BASE_MAP contains the CSS variable → theme path mapping.
 * This is the shared base across all brands.
 */
const BASE_MAP = {
    /* -------------------- FONTS -------------------- */
    '--font-main': 'font.main',
    '--font-alternative': 'font.alternative',
    '--font-alternative-2': 'font.alternative-2',
    '--font-alternative-3': 'font.alternative-3',
    /* -------------------- COLORS -------------------- */
    // Primary
    '--color-primary-100': 'color.primary.100',
    '--color-primary-300': 'color.primary.300',
    '--color-primary-500': 'color.primary.500',
    '--color-primary-700': 'color.primary.700',
    '--color-primary-900': 'color.primary.900',
    // Secondary
    '--color-secondary-100': 'color.secondary.100',
    '--color-secondary-300': 'color.secondary.300',
    '--color-secondary-500': 'color.secondary.500',
    '--color-secondary-700': 'color.secondary.700',
    '--color-secondary-900': 'color.secondary.900',
    // Tertiary
    '--color-tertiary-100': 'color.tertiary.100',
    '--color-tertiary-300': 'color.tertiary.300',
    '--color-tertiary-500': 'color.tertiary.500',
    '--color-tertiary-700': 'color.tertiary.700',
    '--color-tertiary-900': 'color.tertiary.900',
    // Accent
    '--color-accent-100': 'color.accent.100',
    '--color-accent-300': 'color.accent.300',
    '--color-accent-500': 'color.accent.500',
    '--color-accent-700': 'color.accent.700',
    '--color-accent-900': 'color.accent.900',
    // Neutral
    '--color-neutral-100': 'color.neutral.100',
    '--color-neutral-300': 'color.neutral.300',
    '--color-neutral-500': 'color.neutral.500',
    '--color-neutral-700': 'color.neutral.700',
    '--color-neutral-900': 'color.neutral.900',
    // Background
    '--color-background-100': 'color.background.100',
    '--color-background-300': 'color.background.300',
    '--color-background-500': 'color.background.500',
    '--color-background-700': 'color.background.700',
    '--color-background-900': 'color.background.900',
    // Success
    '--color-success-100': 'color.success.100',
    '--color-success-300': 'color.success.300',
    '--color-success-500': 'color.success.500',
    '--color-success-700': 'color.success.700',
    '--color-success-900': 'color.success.900',
    // Warning
    '--color-warning-100': 'color.warning.100',
    '--color-warning-300': 'color.warning.300',
    '--color-warning-500': 'color.warning.500',
    '--color-warning-700': 'color.warning.700',
    '--color-warning-900': 'color.warning.900',
    // Error
    '--color-error-100': 'color.error.100',
    '--color-error-300': 'color.error.300',
    '--color-error-500': 'color.error.500',
    '--color-error-700': 'color.error.700',
    '--color-error-900': 'color.error.900',
    // Info
    '--color-info-100': 'color.info.100',
    '--color-info-300': 'color.info.300',
    '--color-info-500': 'color.info.500',
    '--color-info-700': 'color.info.700',
    '--color-info-900': 'color.info.900',
    // Text
    '--color-text-100': 'color.text.100',
    '--color-text-300': 'color.text.300',
    '--color-text-500': 'color.text.500',
    '--color-text-700': 'color.text.700',
    '--color-text-900': 'color.text.900',
    // Highlight
    '--color-highlight-1': 'color.highlight.1',
    '--color-highlight-2': 'color.highlight.2',
    '--color-highlight-3': 'color.highlight.3',
    '--color-highlight-4': 'color.highlight.4',
    '--color-highlight-5': 'color.highlight.5',
    // Christmas
    '--christmas-1': 'christmas.1',
    '--christmas-2': 'christmas.2',
    '--christmas-3': 'christmas.3',
    '--christmas-4': 'christmas.4',
    '--christmas-5': 'christmas.5',
    '--christmas-6': 'christmas.6',
    '--christmas-7': 'christmas.7',
    // Vip
    '--color-vip-1': 'vip.1',
    '--color-vip-2': 'vip.2',
    /* -------------------- BORDER RADIUS -------------------- */
    '--border-radius-button': 'borderRadius.button.default',
    '--border-radius-button-small': 'borderRadius.button.small',
    '--border-radius-input': 'borderRadius.input.default',
    '--border-radius-input-message': 'borderRadius.input.message',
    '--border-radius-promo-item': 'borderRadius.promotions.item',
    '--border-radius-header-profile': 'borderRadius.header.profile',
    '--border-radius-header-vip': 'borderRadius.header.vip',
    '--border-radius-header-toggle': 'borderRadius.header.toggle',
    '--border-radius-header-chat-notifications': 'borderRadius.header.chatNotifications',
    /* -------------------- LAYOUT -------------------- */
    '--chat-width': 'width.chatWidth',
    /* -------------------- SHADOWS -------------------- */
    '--shadow-neutral': 'boxShadows.neutral',
    '--shadow-inner': 'boxShadows.inner',
    '--shadow-vip': 'boxShadows.vip'
};
// Converts "color.tertiary.500" -> "--color-tertiary-500"
const tokenToCssVarName = (token) => `--${token.replace(/\./g, '-')}`;
// Converts "color.tertiary.500" -> "var(--color-tertiary-500)"
const tokenToVar = (token) => `var(${tokenToCssVarName(token)})`;
const isColorLike = (v) => typeof v === 'string' && (v.startsWith('#') || v.startsWith('rgb(') || v.startsWith('rgba('));
const hexToRgb = (hex) => {
    if (typeof hex !== 'string')
        return null;
    const h = hex.trim();
    // #RRGGBB
    const m6 = /^#([0-9a-f]{6})$/i.exec(h);
    if (m6) {
        const n = parseInt(m6[1], 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    // #RRGGBBAA (alpha ignored — RGB only)
    const m8 = /^#([0-9a-f]{8})$/i.exec(h);
    if (m8) {
        const n = parseInt(m8[1].slice(0, 6), 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    // rgb(...) / rgba(...)
    const mrgb = /^rgba?\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)(?:\s*,\s*([0-9.]+))?\s*\)$/i.exec(h);
    if (mrgb)
        return [Number(mrgb[1]), Number(mrgb[2]), Number(mrgb[3])];
    return null;
};
// Reads the optional alpha byte of an #RRGGBBAA hex, as a 0..1 number. Any other
// input (short hex, #RRGGBB, rgb(), null) has no alpha and returns null.
const hexAlpha = (hex) => {
    if (typeof hex !== 'string')
        return null;
    const m8 = /^#([0-9a-f]{6})([0-9a-f]{2})$/i.exec(hex.trim());
    if (!m8)
        return null;
    return Math.round((parseInt(m8[2], 16) / 255) * 100);
};
const rgbToHsl = (r, g, b) => {
    const [rn, gn, bn] = [r, g, b].map((c) => c / 255);
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case rn:
                h = (gn - bn) / d + (gn < bn ? 6 : 0);
                break;
            case gn:
                h = (bn - rn) / d + 2;
                break;
            default:
                h = (rn - gn) / d + 4;
        }
        h /= 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};
/** White overlay at `opacity` (0–1) composited onto an opaque background → opaque RGB. */
const compositeWhiteOverlay = (bg, opacity) => [
    Math.round(bg[0] + (255 - bg[0]) * opacity),
    Math.round(bg[1] + (255 - bg[1]) * opacity),
    Math.round(bg[2] + (255 - bg[2]) * opacity)
];
// Surface slots must be opaque HSL — the SDK uses `hsl(var(--sb-card))` and cannot
// render a translucent white overlay as a visible elevation on a flat `--sb-background`.
const OPAQUE_SURFACE_SLOTS = new Set(['card', 'outcome']);
/**
 * Converts a hex/rgb color to the space-separated HSL components the sportsbook
 * SDK expects (see iframe-integration-guide.md §4), e.g. "220 27% 6%". When the
 * source carries an alpha byte (#RRGGBBAA) it is appended as "H S% L% / A%",
 * matching the guide's own `--sb-border: 0 0% 100% / 10%;` example. Returns null
 * for unresolved input.
 */
const hexToHsl = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return null;
    const components = rgbToHsl(...rgb);
    const alpha = hexAlpha(hex);
    return alpha === null ? components : `${components} / ${alpha}%`;
};
/**
 * Resolves a brand theme's `components.sportmodity` tokens into `--sb-*` CSS
 * variables (HSL components) for injection into the sportsbook iframe. Each slot
 * value is resolved from the palette and converted; unresolved slots are skipped
 * so we never emit `--sb-x: undefined`. Returns {} when the brand has no block.
 */
const buildSportmodityColorVars = (brandTheme) => {
    const sportmodity = brandTheme?.components?.sportmodity;
    if (!sportmodity)
        return {};
    const bgResolved = get(brandTheme, sportmodity.background, sportmodity.background);
    const bgRgb = bgResolved ? hexToRgb(bgResolved) : null;
    const out = {};
    for (const [slot, token] of Object.entries(sportmodity)) {
        const resolved = get(brandTheme, token, token);
        if (!resolved)
            continue;
        if (slot === 'radius') {
            out[`--sb-${slot}`] = resolved;
            continue;
        }
        const alpha = hexAlpha(resolved);
        if (alpha !== null && bgRgb && OPAQUE_SURFACE_SLOTS.has(slot)) {
            out[`--sb-${slot}`] = rgbToHsl(...compositeWhiteOverlay(bgRgb, alpha / 100));
            continue;
        }
        const hsl = hexToHsl(resolved);
        if (hsl)
            out[`--sb-${slot}`] = hsl;
    }
    return out;
};
/**
 * Constructs gradient CSS strings using color, christmas, or other allowed
 * tokens and returns a map { '--gradient-<name>': 'linear-gradient(...)' }.
 */
const ALLOWED_TOKEN_PREFIXES = ['color.', 'christmas.', 'vip.'];
const buildGradientVars = (brandTheme) => {
    const grads = brandTheme?.gradients || {};
    const out = {};
    for (const [name, def] of Object.entries(grads)) {
        if (!def || !def.type)
            continue;
        let css = '';
        if (def.type === 'raw') {
            if (typeof def.css !== 'string' || !def.css.trim()) {
                console.warn(`Gradient "${name}" with type "raw" is missing a valid "css" string.`);
                continue;
            }
            css = def.css.trim();
        }
        else {
            const stopsArr = def.stops;
            if (!Array.isArray(stopsArr)) {
                console.warn(`Gradient "${name}" of type "${def.type}" is missing "stops".`);
                continue;
            }
            const stopsCss = stopsArr.map((s) => {
                if (!s || !s.token || typeof s.token !== 'string') {
                    throw new Error(`The gradient "${name}" uses an invalid stop token.`);
                }
                const hasValidPrefix = ALLOWED_TOKEN_PREFIXES.some((prefix) => s.token.startsWith(prefix));
                if (!hasValidPrefix) {
                    throw new Error(`The gradient "${name}" uses an invalid stop token (${s.token}). It must start with one of: ${ALLOWED_TOKEN_PREFIXES.join(', ')}`);
                }
                const pos = s.at || '';
                return `${tokenToVar(s.token)}${pos ? ` ${pos}` : ''}`;
            });
            if (def.type === 'linear') {
                const angle = def.angle || '180deg';
                css = `linear-gradient(${angle}, ${stopsCss.join(', ')})`;
            }
            else if (def.type === 'radial') {
                const shape = def.shape || 'circle';
                const at = def.at
                    ? ` at ${def.at}`
                    : '';
                css = `radial-gradient(${shape}${at}, ${stopsCss.join(', ')})`;
            }
            else if (def.type === 'conic') {
                const from = def.from
                    ? ` from ${def.from}`
                    : '';
                const at = def.at
                    ? ` at ${def.at}`
                    : '';
                css = `conic-gradient(${from}${at}${from || at ? ', ' : ''}${stopsCss.join(', ')})`;
            }
            else {
                console.warn(`Gradient "${name}" has unsupported type "${def.type}".`);
                continue;
            }
        }
        out[`--gradient-${name}`] = css;
    }
    return out;
};
/* ------------------------------------------------------------------ */
/* Components → CSS variables                                          */
/* ------------------------------------------------------------------ */
/**
 * Flattens a nested object using hyphen-joined keys.
 * Example:
 * { progress: { background: 'color.x', 'fill-2': 'color.y' } }
 * -> { 'progress-background': 'color.x', 'progress-fill-2': 'color.y' }
 */
const flattenObject = (obj, prefix = '') => {
    const out = {};
    for (const [key, val] of Object.entries(obj || {})) {
        const flatKey = prefix ? `${prefix}-${key}` : key;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            Object.assign(out, flattenObject(val, flatKey));
        }
        else {
            out[flatKey] = val;
        }
    }
    return out;
};
/**
 * Builds CSS variables from theme.components (nested).
 * - color.*    -> var(--color-...)
 * - gradient.* -> var(--gradient-...)
 * - other values (hex, rgba, etc.) are kept as-is
 * Output variable name: --component-<flattened-path>
 */
const buildComponentVars = (brandTheme) => {
    const components = brandTheme?.components || {};
    const flat = flattenObject(components);
    const out = {};
    for (const [name, token] of Object.entries(flat)) {
        if (token == null)
            continue;
        let cssValue = token;
        if (typeof token === 'string') {
            if (token.startsWith('color.')) {
                cssValue = tokenToVar(token);
            }
            else if (token.startsWith('gradient.')) {
                const gradVarName = `--${token.replace(/\./g, '-')}`;
                cssValue = `var(${gradVarName})`;
            }
        }
        out[`--component-${name}`] = cssValue;
    }
    return out;
};
/**
 * buildVars() applies BASE_MAP + overrides on a theme.
 * It also augments with gradient variables and component variables.
 */
const buildVars = (brandTheme, extra = {}) => {
    const map = { ...BASE_MAP, ...extra };
    const out = {};
    for (const [cssVar, path] of Object.entries(map)) {
        const val = get(brandTheme, path);
        out[cssVar] = Array.isArray(val)
            ? val.map((f) => (/\s/.test(f) && !/^["']/.test(f) ? `"${f}"` : f)).join(', ')
            : val;
        // auto-generates: --color-xxxx-rgb = "r g b"
        if (cssVar.startsWith('--color-') && isColorLike(val)) {
            const rgb = hexToRgb(val);
            if (rgb)
                out[`${cssVar}-rgb`] = `${rgb[0]} ${rgb[1]} ${rgb[2]}`;
        }
    }
    Object.assign(out, buildGradientVars(brandTheme));
    Object.assign(out, buildComponentVars(brandTheme));
    return out;
};
// Build the final root objects for each theme
export const root = buildVars(FLUSH_THEME);
export const rootFomo = buildVars(FOMOIO_THEME);
export const rootLuna = buildVars(LUNA_THEME);
export const rootRocketbet = buildVars(ROCKETBET_THEME);
export const rootTurboslots = buildVars(TURBOSLOTS_THEME);
export const rootGeneric = buildVars(GENERIC_THEME);
/**
 * THEMES is the exported registry used by Tailwind configs and the app.
 */
export const THEMES = {
    flush: { ui: FLUSH_THEME, root },
    fomo: { ui: FOMOIO_THEME, root: rootFomo },
    generic: { ui: GENERIC_THEME, root: rootGeneric },
    luna: { ui: LUNA_THEME, root: rootLuna },
    rocketbet: { ui: ROCKETBET_THEME, root: rootRocketbet },
    turboslots: { ui: TURBOSLOTS_THEME, root: rootTurboslots }
};
export const getRootTheme = (themeName) => {
    return THEMES[themeName]?.root ?? THEMES.flush.root;
};
/**
 * `--sb-*` sportsbook color variables (HSL components) for a brand, resolved from
 * its palette. Consumed by the sportmodity embed to theme the vendor iframe.
 */
export const getSportmodityColorVars = (themeName) => {
    return buildSportmodityColorVars(THEMES[themeName]?.ui ?? THEMES.flush.ui);
};
export const getTheme = (themeName) => {
    const uiTheme = THEMES[themeName]?.ui ?? THEMES.flush.ui;
    return {
        extend: {
            boxShadow: {
                vip: uiTheme.boxShadows.vip
            },
            minHeight: {
                fit: uiTheme.height.height15
            },
            minWidth: {
                fit: uiTheme.width.fitContent
            },
            maxHeight: {
                0: uiTheme.maxHeight.zero,
                full: uiTheme.maxHeight.full
            },
            transitionProperty: {
                'max-height': uiTheme.transitions.maxHeight
            }
        }
    };
};
